import { cloneElement, useEffect, useId, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Flex, DropdownDownIcon } from 'src/components';
import { colors } from 'src/theme';

const AccordionStyle = styled.div({
  width: '100%',
})

const AccordionHeaderStyle = styled.button((props: { expandable: boolean }) => ({
  width: '100%',
  cursor: props.expandable ? 'pointer' : 'default',
  background: 'none',
  border: 0,
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left' as const,
  '&:focus-visible': {
    outline: `2px solid ${colors.primary}`,
    outlineOffset: '4px',
    borderRadius: '4px',
  },
}))

const HeadingStyle = styled.div({
  margin: 0,
  fontSize: 'inherit',
  fontWeight: 'inherit',
})

const AccordionBodyStyle = styled.div((props: { isOpen: boolean, maxHeight: number | null, gap: number | string }) => ({
  // eslint-disable-next-line no-nested-ternary
  maxHeight: props.isOpen ? (props.maxHeight ? `${props.maxHeight}px` : 'auto') : '0px',
  overflow: 'hidden',
  transition: props.isOpen
    ? 'max-height 0.3s ease, margin-top 0.6s ease'
    : 'max-height 0.3s ease, margin-top 0.6s ease, visibility 0s linear 0.3s',
  marginTop: props.isOpen ? props.gap : 0,
  // Keeps collapsed content out of the tab order and the accessibility tree
  visibility: (props.isOpen ? 'visible' : 'hidden') as 'visible' | 'hidden',
}))

const FlexItem = styled.div({
  flex: 1
})

const FlexItem2 = styled.div({
  flex: 0,
  alignSelf: 'center',
})

export interface AccordionProps {
  title: string | React.JSX.Element | React.JSX.Element[];
  content: string | React.JSX.Element | React.JSX.Element[];
  key?: number;
  icon?: React.ReactNode;
  expandIcon?: boolean;
  expandable?: boolean;
  open?: boolean;
  gap?: number | string;
  onChange?: () => void;
  expandIconComponent?: React.ReactElement;
  maxHeight?: number;
  className?: string;
  isLazy?: boolean;
  // Wraps the header button in a heading of this level
  headingLevel?: 2 | 3 | 4;
}

export const Accordion = ({ icon, title, content, open = false, expandable = true, expandIcon = true, gap = 0, onChange, expandIconComponent = <DropdownDownIcon />, maxHeight, className, isLazy = false, headingLevel }: AccordionProps) => {
  const contentId = useId();
  const [isOpen, setIsOpen] = useState(open);
  const [maxHeightValue, setMaxHeightValue] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeightValue(contentRef.current.scrollHeight);
    }
  }, [content, contentRef.current?.scrollHeight]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
    if (onChange) onChange();
  };

  return (
    <AccordionStyle className={className}>
      <HeadingStyle as={headingLevel ? (`h${headingLevel}` as const) : 'div'}>
        <AccordionHeaderStyle
          type="button"
          onClick={() => expandable && toggleAccordion()}
          expandable={expandable}
          aria-expanded={expandable ? isOpen : undefined}
          aria-controls={expandable ? contentId : undefined}
          disabled={!expandable}
        >
          <Flex justify='space-between'>
            {icon ? <>{icon}</> : <></>}
            <FlexItem>
              {title}
            </FlexItem>
            {expandIcon ? <FlexItem2 aria-hidden="true">{cloneElement(expandIconComponent, { isOpen })}</FlexItem2> : <></>}
          </Flex>
        </AccordionHeaderStyle>
      </HeadingStyle>
      <AccordionBodyStyle id={contentId} isOpen={isOpen} maxHeight={maxHeight || maxHeightValue} ref={contentRef} gap={gap}>
        {!isLazy || isOpen ? content : null}
      </AccordionBodyStyle>
    </AccordionStyle>
  );
};
